import RouteConstants from '@/features/router/RouteConstants';
import { useWorkspace } from '@/features/workspaces/hooks';
import { PageHeader, PageLayout } from '@/global/components';
import { Loadable } from '@/components/loaders';
import { BreadcrumbItem } from '@/lib/Breadcrumbs/types';
import { CreateStepper, useCreateStepper } from '@/lib/Steppers';
import { BaseAudience } from '@/models/audiences';
import { ConnectorFlowDTO, CustomConnectorDto } from '@/models/connectors';
import { useGetAudienceQuery } from '@/services/endpoints/audiences';
import { FC, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { CREATE_CUSTOM_CONNECTOR_STEPS } from './constants';
import { CustomConnectorAudienceSelectionStep, CustomConnectorFlowStep, CustomConnectorSelectionStep, CustomConnectorSyncStep } from './steps';

const breadcrumbs: BreadcrumbItem[] = [
  {
    label: 'ALL.destinations.title',
    path: RouteConstants.destinations,
  },
  {
    label: 'Create Custom Destination',
    path: RouteConstants.createCustomDestination,
  },
];

export const CreateCustomDestinationContent: FC = () => {
  const workspace = useWorkspace();
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const audienceId = searchParams.get('audienceId');

  const { data: audienceFromUrl, isLoading: isAudienceLoading } = useGetAudienceQuery(
    {
      workspaceId: workspace.id,
      audienceId: audienceId!,
    },
    { skip: !audienceId }
  );

  const initialStepId = audienceId ? 'selectCustomConnector' : undefined;

  const { steps, currentStepId, currentStepIndex, goToNextStep, completeCurrentStep, completeStep } = useCreateStepper(
    CREATE_CUSTOM_CONNECTOR_STEPS,
    initialStepId
  );

  const [selectedAudience, setSelectedAudience] = useState<BaseAudience>();
  const [selectedConnector, setSelectedConnector] = useState<CustomConnectorDto>();
  const [selectedFlow, setSelectedFlow] = useState<ConnectorFlowDTO>();
  const [flowParams, setFlowParams] = useState<Record<string, any>>({});
  const [flowMappings, setFlowMappings] = useState<Record<string, any>>({});

  useEffect(() => {
    if (audienceFromUrl) {
      setSelectedAudience(audienceFromUrl);
      completeStep('selectCustomConnectorAudience');
    }
  }, [audienceFromUrl, completeStep]);
  const goToDestinations = () => {
    history.push(RouteConstants.destinations);
  };

  const handleCancel = () => {
    goToDestinations();
  };

  const completeAndGoNextStep = () => {
    completeCurrentStep();
    goToNextStep();
  };

  // Custom connector handlers - similar to common destination creation
  const goToSelectPropertiesStep = (audience: BaseAudience, connector: CustomConnectorDto, flow: ConnectorFlowDTO) => {
    setSelectedAudience(audience);
    setSelectedConnector(connector);
    setSelectedFlow(flow);

    completeAndGoNextStep();
  };

  const goToSyncStep = (params: Record<string, any>, mappings: Record<string, any>) => {
    setFlowParams(params);
    setFlowMappings(mappings);
    completeAndGoNextStep();
  };

  const handleSyncSuccessful = () => {
    goToDestinations();
  };

  const stepTemplate = () => {
    switch (currentStepId) {
      case 'selectCustomConnectorAudience':
        return (
          <CustomConnectorAudienceSelectionStep
            onNext={(audience) => {
              setSelectedAudience(audience);
              completeAndGoNextStep();
            }}
            onCancel={handleCancel}
          />
        );

      case 'selectCustomConnector':
        return (
          <Loadable isLoading={audienceId ? isAudienceLoading : false}>
            {selectedAudience ? (
              <CustomConnectorSelectionStep
                audience={selectedAudience}
                onNext={(connector, flow) => goToSelectPropertiesStep(selectedAudience!, connector, flow)}
                onCancel={handleCancel}
              />
            ) : (
              <div className='flex grow flex-col items-center justify-center p-6'>
                <p className='text-gray-500'>No audience selected</p>
              </div>
            )}
          </Loadable>
        );

      case 'configureCustomConnectorFlow':
        if (!selectedAudience || !selectedConnector || !selectedFlow) {
          return <div className='flex grow flex-col items-center justify-center p-6'>Missing required data</div>;
        }
        return (
          <CustomConnectorFlowStep
            audience={selectedAudience}
            connector={selectedConnector}
            flow={selectedFlow}
            onNext={goToSyncStep}
            onCancel={handleCancel}
          />
        );

      case 'sync':
        if (!selectedAudience || !selectedConnector || !selectedFlow) {
          return <div className='flex grow flex-col items-center justify-center p-6'>Missing required data</div>;
        }
        return (
          <CustomConnectorSyncStep
            audience={selectedAudience}
            connector={selectedConnector}
            flow={selectedFlow}
            params={flowParams}
            mappings={flowMappings}
            onSyncSuccessful={handleSyncSuccessful}
            onCancel={handleCancel}
          />
        );

      default:
        return null;
    }
  };

  return (
    <PageLayout>
      <PageHeader breadcrumbs={breadcrumbs} title='ALL.destinations.title' subtitle='ALL.destinations.createSubtitle' />
      <CreateStepper steps={steps} currentStepIndex={currentStepIndex} />
      {stepTemplate()}
    </PageLayout>
  );
};
